import type { ReactNode } from "react";
import { Box, Container, Divider, Flex, Group, SimpleGrid, Skeleton, Stack } from "@mantine/core";
import {
  COMPACT_COMPANY_GRID_COLS,
  DEFAULT_PAGE_SIZE,
  DISCOVER_PAGE_CONTENT_PX,
} from "@/pages/discover/DiscoverPage.constants";
import { splitAfterFirstRow } from "@/pages/discover/splitAfterFirstRow.util";
import { PAGE_PADDING_X } from "@/shared/constants/layout.constants";
import { LANDING_NAVBAR_HEIGHT } from "@/components/layout/LandingNavbar";
import {
  SIDEBAR_GRIDLINE_OVERLAY,
  SIDEBAR_WIDTH_EXPANDED_PX,
} from "@/components/layout/sidebarLayout.const";

const CARD_BORDER = "1px solid #E2E8F0";
const HERO_GRADIENT = "linear-gradient(180deg, #EAF4FB 0%, #F4F9FD 55%, #FFFFFF 100%)";
const HERO_SHELL_BORDER = "1px solid #D6E8F5";
const AUTH_HEADER_HEIGHT = 64;

/** Compact Discover company card placeholder — matches `DiscoverCompanyCard` denser layout. */
export function DiscoverCompanyCardShimmerItem() {
  return (
    <Box
      p="sm"
      h="100%"
      style={{
        border: CARD_BORDER,
        borderRadius: 16,
        backgroundColor: "#FFFFFF",
        boxShadow: "0 4px 16px rgba(15, 23, 42, 0.06)",
      }}
    >
      <Stack gap="xs" h="100%">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap={4}>
          <Stack gap={4} miw={0} style={{ flex: 1 }}>
            <Skeleton height={12} width="72%" radius="sm" />
            <Skeleton height={16} width={22} radius="sm" />
          </Stack>
          <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Skeleton height={22} width={72} radius="xl" />
            <Skeleton height={28} width={28} radius="md" />
          </Group>
        </Group>

        <Skeleton height={16} width="92%" radius="sm" />
        <Skeleton height={12} width="58%" radius="sm" />

        <Box
          p="xs"
          style={{
            borderRadius: 10,
            backgroundColor: "#F0FDF4",
          }}
        >
          <Skeleton height={10} width="40%" radius="sm" mb={6} />
          <Skeleton height={12} width="88%" radius="sm" mb={4} />
          <Skeleton height={12} width="70%" radius="sm" />
        </Box>

        <Stack gap={4} mt="auto">
          <Skeleton height={11} width="65%" radius="sm" />
          <Skeleton height={11} width="55%" radius="sm" />
        </Stack>

        <Skeleton height={32} radius="md" mt={4} />
      </Stack>
    </Box>
  );
}

export function DiscoverCompanyCardsShimmer({
  count = DEFAULT_PAGE_SIZE,
  afterFirstRow,
  firstRowCount,
}: {
  count?: number;
  afterFirstRow?: ReactNode;
  firstRowCount?: number;
}) {
  const cards = Array.from({ length: count }).map((_, idx) => (
    <Box key={`discover-shimmer-card-${idx}`} style={{ display: "flex", width: "100%", minHeight: 220 }}>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <DiscoverCompanyCardShimmerItem />
      </Box>
    </Box>
  ));

  const grid = (items: ReactNode[]) => (
    <SimpleGrid type="container" cols={COMPACT_COMPANY_GRID_COLS} spacing="sm" w="100%">
      {items}
    </SimpleGrid>
  );

  const split =
    afterFirstRow && firstRowCount && cards.length > 0
      ? splitAfterFirstRow(cards, firstRowCount)
      : null;

  if (!split) return grid(cards);

  return (
    <>
      {grid(split.firstRow)}
      {afterFirstRow}
      {split.rest.length > 0 ? grid(split.rest) : null}
    </>
  );
}

function DiscoverHeroSkeleton({ panelCount = 4 }: { panelCount?: number }) {
  return (
    <Box
      w="100%"
      style={{ background: HERO_GRADIENT }}
      pt={{ base: "xs", sm: 10 }}
      pb={0}
    >
      <Container fluid px={DISCOVER_PAGE_CONTENT_PX}>
        <Box
          p="sm"
          style={{
            borderRadius: 24,
            background: "linear-gradient(180deg, #EAF4FB 0%, #F8FBFF 50%, #FFFFFF 100%)",
            border: HERO_SHELL_BORDER,
          }}
        >
          <SimpleGrid
            cols={{ base: 1, sm: panelCount >= 4 ? 2 : 1, xl: panelCount }}
            spacing={6}
          >
            {Array.from({ length: panelCount }).map((_, i) => (
              <Box
                key={`hero-panel-${i}`}
                p={10}
                style={{
                  height: 260,
                  background: "#FFFFFF",
                  border: CARD_BORDER,
                  borderRadius: 14,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <Group gap={8} wrap="nowrap">
                  <Skeleton height={28} width={28} radius="sm" />
                  <Skeleton height={14} style={{ flex: 1 }} radius="sm" />
                </Group>
                <Skeleton height={10} width="78%" radius="sm" />
                <Skeleton height="100%" radius="md" style={{ flex: 1, minHeight: 120 }} />
                {i === 0 && panelCount >= 4 ? (
                  <Stack gap={4} mt="auto">
                    <Skeleton height={30} radius="md" />
                    <Skeleton height={28} radius="md" />
                  </Stack>
                ) : null}
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      </Container>
    </Box>
  );
}

function DiscoverEcosystemsSkeleton() {
  return (
    <Stack gap="sm" mt={8} mb="lg" align="center" w="100%">
      <Skeleton height={16} width={220} radius="sm" />
      <Skeleton height={72} radius="md" w="100%" maw={520} />
    </Stack>
  );
}

function DiscoverResultsSkeleton() {
  return (
    <Stack gap="md">
      <Box>
        <Skeleton height={16} width={140} radius="sm" mb={8} />
        <Divider color="slate.2" />
      </Box>

      <Flex
        direction={{ base: "column", sm: "row" }}
        align={{ base: "stretch", sm: "flex-end" }}
        gap="sm"
      >
        <Skeleton height={42} radius="md" style={{ flex: 1 }} />
        <Skeleton height={42} width={170} radius="md" visibleFrom="sm" />
        <Skeleton height={42} radius="md" hiddenFrom="sm" />
      </Flex>

      <Box mt="sm" style={{ containerType: "inline-size" }}>
        <DiscoverCompanyCardsShimmer count={DEFAULT_PAGE_SIZE} />
      </Box>
    </Stack>
  );
}

/**
 * Guest Discover body placeholder — mirrors hero → ecosystems → search → company cards.
 * Use `withNavOffset` when the real landing chrome is not already painted.
 */
export function DiscoverBodySkeleton({
  withNavOffset = true,
  variant = "guest",
}: {
  withNavOffset?: boolean;
  variant?: "guest" | "auth";
}) {
  return (
    <Box
      style={{
        paddingTop: withNavOffset ? LANDING_NAVBAR_HEIGHT : 0,
        paddingBottom: 32,
        background: "#ffffff",
      }}
    >
      <DiscoverHeroSkeleton panelCount={variant === "auth" ? 3 : 4} />
      <Container fluid px={DISCOVER_PAGE_CONTENT_PX} pt={0} pb={0}>
        <DiscoverEcosystemsSkeleton />
        <DiscoverResultsSkeleton />
      </Container>
    </Box>
  );
}

function AuthSidebarShellSkeleton() {
  return (
    <Box
      component="aside"
      visibleFrom="sm"
      style={{
        width: SIDEBAR_WIDTH_EXPANDED_PX,
        flexShrink: 0,
        minHeight: "100dvh",
        backgroundColor: "var(--mantine-color-primary-6)",
        backgroundImage: SIDEBAR_GRIDLINE_OVERLAY,
        borderRight: "1px solid rgba(255, 255, 255, 0.14)",
        display: "flex",
        flexDirection: "column",
        padding: "16px 12px",
        gap: 8,
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap" mb="sm">
        <Skeleton height={36} width={120} radius="sm" style={{ opacity: 0.55 }} />
        <Skeleton height={28} width={28} radius="md" style={{ opacity: 0.45 }} />
      </Group>
      <Divider color="rgba(255, 255, 255, 0.35)" mb="sm" />

      <Stack gap={8} style={{ flex: 1 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton
            key={`auth-nav-${i}`}
            height={40}
            radius="md"
            style={{ opacity: 0.35 }}
          />
        ))}
      </Stack>

      <Stack gap={8} mt="auto" pt="md">
        <Skeleton height={48} radius="md" style={{ opacity: 0.4 }} />
        <Skeleton height={36} radius="md" style={{ opacity: 0.3 }} />
      </Stack>
    </Box>
  );
}

function AuthHeaderShellSkeleton() {
  return (
    <Box
      component="header"
      style={{
        height: AUTH_HEADER_HEIGHT,
        flexShrink: 0,
        borderBottom: "1px solid #E5E7EB",
        backgroundColor: "#fff",
        display: "flex",
        alignItems: "center",
        padding: "0 16px",
      }}
    >
      <Group justify="space-between" w="100%" wrap="nowrap">
        <Group gap={8} wrap="nowrap" visibleFrom="sm">
          <Skeleton height={34} width={100} radius="xl" />
          <Skeleton height={34} width={88} radius="xl" />
          <Skeleton height={34} width={96} radius="xl" />
          <Skeleton height={34} width={72} radius="xl" />
        </Group>
        <Group gap={8} wrap="nowrap" hiddenFrom="sm">
          <Skeleton height={32} width={96} radius="sm" />
          <Skeleton height={28} width={28} radius="sm" />
        </Group>
        <Group gap={10} wrap="nowrap" ml="auto">
          <Skeleton height={32} width={32} radius="xl" />
          <Skeleton height={32} width={32} radius="xl" />
          <Skeleton height={32} width={120} radius="md" visibleFrom="sm" />
        </Group>
      </Group>
    </Box>
  );
}

/**
 * Full-page auth gate fallback: AppShell-shaped sidebar + header + Discover body.
 * Used while session/user hydrates before `AppLayout` mounts.
 */
export function AuthAppShellSkeleton() {
  return (
    <Flex style={{ minHeight: "100dvh", background: "#ffffff" }}>
      <AuthSidebarShellSkeleton />
      <Flex direction="column" style={{ flex: 1, minWidth: 0, minHeight: "100dvh" }}>
        <AuthHeaderShellSkeleton />
        <Box style={{ flex: 1, overflow: "auto" }}>
          <DiscoverBodySkeleton withNavOffset={false} variant="auth" />
        </Box>
      </Flex>
    </Flex>
  );
}

/** Full-page Suspense / auth-gate chrome matching LandingNavbar. */
export function DiscoverTopNavSkeleton() {
  return (
    <Box
      component="header"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 50,
        background: "#ffffff",
        borderBottom: "1px solid #e4e8ee",
        height: LANDING_NAVBAR_HEIGHT,
      }}
    >
      <Box px={PAGE_PADDING_X} w="100%">
        <Flex align="center" justify="space-between" style={{ height: LANDING_NAVBAR_HEIGHT, gap: 12 }}>
          <Skeleton height={36} width={96} radius="sm" style={{ flexShrink: 0 }} />

          <Group
            visibleFrom="sm"
            gap={8}
            wrap="nowrap"
            style={{ flex: 1, justifyContent: "center" }}
          >
            <Skeleton height={36} width={100} radius="xl" />
            <Skeleton height={36} width={88} radius="xl" />
            <Skeleton height={36} width={96} radius="xl" />
            <Skeleton height={36} width={72} radius="xl" />
          </Group>

          <Group gap={10} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Skeleton height={40} width={88} radius="md" />
            <Skeleton height={40} width={100} radius="md" visibleFrom="sm" />
          </Group>
        </Flex>
      </Box>
    </Box>
  );
}
